(function () {
  const DATA_CACHE = new Map();
  const RECENT_KEY = 'rsv_help_recent_v2';
  const STAGED_PAGE_HANDLES = new Set([
    'diy-stone-veneer-installation',
    'stone-veneer-exposed-to-water',
    'prepare-wall-for-stone-veneer',
    'how-much-stone-veneer-to-order',
    'stone-veneer-mortar-or-adhesive'
  ]);

  const HOME_FILTERS = [
    {
      id: 'all',
      label: 'Start here',
      title: 'Recommended answers',
      description: 'Popular answers for shoppers comparing stone, planning installation, or trying to avoid a costly mistake.',
      match: () => true
    },
    {
      id: 'installation',
      label: 'Installation',
      title: 'Installation answers',
      description: 'Prep, mortar, layout, DIY decisions, and what needs to happen before stone goes on the wall.',
      match: (article) => article.categoryId === 'installation' || article.journeyStage === 'install' || article.articleType === 'how-to'
    },
    {
      id: 'projects',
      label: 'Projects',
      title: 'Project answers',
      description: 'Find answers by the kind of feature you are building: fireplaces, foundations, columns, steps, and more.',
      match: (article) => article.categoryId === 'projects' || includesAny(article.projectType, ['fireplace', 'exterior', 'interior'])
    },
    {
      id: 'choosing-stone',
      label: 'Choosing stone',
      title: 'Choosing stone',
      description: 'Profiles, colors, samples, style decisions, and how to avoid choosing stone that looks wrong at home.',
      match: (article) => article.journeyStage === 'choose-stone' || article.categoryId === 'design-products' || includesAny(article.productFamily, ['sample-kit'])
    },
    {
      id: 'measuring',
      label: 'Measuring',
      title: 'Measuring and ordering',
      description: 'Square footage, corners, waste, samples, shipping, and order planning.',
      match: (article) => article.journeyStage === 'measure-order' || article.categoryId === 'care-cost-ordering'
    },
    {
      id: 'exterior-water',
      label: 'Exterior/water',
      title: 'Exterior and water answers',
      description: 'Moisture, exterior exposure, drainage, sealing, and surface suitability questions.',
      match: (article) => article.clusterId === 'water-exterior-exposure' || article.environment === 'exterior' || compact(article.terms).includes('water')
    },
    {
      id: 'care',
      label: 'Care',
      title: 'Care and maintenance',
      description: 'Cleaning, sealing, repair, durability, and long-term ownership questions.',
      match: (article) => article.journeyStage === 'care-maintain' || compact(article.terms).includes('clean') || compact(article.terms).includes('seal')
    },
    {
      id: 'troubleshooting',
      label: 'Troubleshooting',
      title: 'Troubleshooting answers',
      description: 'Mistakes, edge cases, repairs, and when a project needs professional judgment.',
      match: (article) => article.journeyStage === 'troubleshoot' || article.articleType === 'troubleshooting' || article.riskLevel === 'high'
    }
  ];

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);
  }

  function compact(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function includesAny(value, needles) {
    const list = Array.isArray(value) ? value : [value];
    return needles.some((needle) => list.includes(needle));
  }

  function readStored(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      return [];
    }
  }

  function pushRecent(id) {
    if (!id) return;
    try {
      const next = readStored(RECENT_KEY).filter((item) => item !== id);
      next.unshift(id);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next.slice(0, 12)));
    } catch (error) {}
  }

  function track(eventName, detail) {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
    if (window.posthog && window.posthog.capture) {
      window.posthog.capture(eventName, detail);
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function markReady(root) {
    if (!root) return;
    requestAnimationFrame(() => root.classList.add('rhl-is-ready'));
  }

  function wireNavigationMotion(root) {
    if (!root || prefersReducedMotion()) return;
    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.target || link.hasAttribute('download')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      const nextUrl = new URL(link.href, window.location.href);
      if (nextUrl.origin !== window.location.origin || nextUrl.href === window.location.href) return;

      event.preventDefault();
      root.classList.add('rhl-is-leaving');
      window.setTimeout(() => {
        window.location.href = nextUrl.href;
      }, 55);
    });
  }

  function loadData(url) {
    if (!url) return Promise.reject(new Error('Missing RSV help library data URL'));
    if (!DATA_CACHE.has(url)) {
      DATA_CACHE.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Failed loading help library data: ${response.status}`);
        return response.json();
      }));
    }
    return DATA_CACHE.get(url);
  }

  function articles(data) {
    return data.search || data.articles || [];
  }

  function articleMap(data) {
    return new Map(articles(data).map((article) => [article.id, article]));
  }

  function articleByIdOrSlug(data, idOrSlug) {
    if (!idOrSlug) return null;
    const map = articleMap(data);
    if (map.has(idOrSlug)) return map.get(idOrSlug);
    return articles(data).find((article) => article.slug === idOrSlug || article.href?.endsWith(`/${idOrSlug}/`)) || null;
  }

  function categoryLabel(data, id, fallback) {
    const category = (data.navigation?.categories || []).find((item) => item.id === id || item.label === id);
    return category?.label || fallback || id || '';
  }

  function articleUrl(article) {
    const handle = article.slug || article.handle || article.id;
    if (handle && STAGED_PAGE_HANDLES.has(handle)) return `/pages/${handle}`;
    return article.href || '#';
  }

  function scoreArticle(article, query, activeFilter) {
    let score = 0;
    const filter = HOME_FILTERS.find((item) => item.id === activeFilter) || HOME_FILTERS[0];
    const haystack = compact(article.terms || `${article.title} ${article.excerpt} ${article.intent} ${article.category} ${article.cluster}`);
    const tokens = compact(query).split(/\s+/).filter(Boolean);

    if (filter.id !== 'all' && !filter.match(article)) return -1;

    if (tokens.length) {
      for (const token of tokens) {
        if (!haystack.includes(token)) return -1;
        if (compact(article.title).includes(token)) score += 18;
        if (compact(article.intent).includes(token)) score += 10;
        if (compact(article.excerpt).includes(token)) score += 7;
        score += 1;
      }
    }

    if (filter.id === 'all') {
      if (article.priority === 1) score += 12;
      if (['diy-stone-veneer-installation', 'stone-veneer-exposed-to-water', 'stone-veneer-samples', 'how-much-stone-veneer-to-order'].includes(article.id)) score += 18;
    }

    score += Math.max(0, 12 - Number(article.priority || 12));
    return score;
  }

  function labelForArticle(data, article) {
    const category = categoryLabel(data, article.categoryId, article.category);
    return [category, article.cluster].filter(Boolean).join(' · ');
  }

  function tokenize(query) {
    return compact(query).split(/\s+/).filter(Boolean);
  }

  function highlightText(value, tokens) {
    let html = escapeHtml(value || '');
    const safeTokens = uniqueArticles(tokens.map((token) => ({ id: token }))).map((item) => item.id).filter((token) => token.length > 1);
    safeTokens.forEach((token) => {
      const pattern = new RegExp(`(${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      html = html.replace(pattern, '<mark>$1</mark>');
    });
    return html;
  }

  function snippetForArticle(article, tokens) {
    const fields = [article.excerpt, article.intent, article.title, article.terms].filter(Boolean);
    const field = fields.find((value) => tokens.some((token) => compact(value).includes(token))) || article.excerpt || article.intent || '';
    if (!field) return '';
    if (!tokens.length || field.length <= 150) return field;
    const normalized = compact(field);
    const firstToken = tokens.find((token) => normalized.includes(token));
    if (!firstToken) return field.slice(0, 150);
    const rawIndex = field.toLowerCase().indexOf(firstToken);
    const start = Math.max(0, rawIndex - 48);
    const end = Math.min(field.length, rawIndex + 118);
    return `${start > 0 ? '…' : ''}${field.slice(start, end).trim()}${end < field.length ? '…' : ''}`;
  }

  function autocompleteScore(article, query, activeFilter) {
    const tokens = tokenize(query);
    const filter = HOME_FILTERS.find((item) => item.id === activeFilter) || HOME_FILTERS[0];
    if (!tokens.length) {
      let score = Math.max(0, 18 - Number(article.priority || 18));
      if (filter.id !== 'all' && filter.match(article)) score += 12;
      if (['diy-stone-veneer-installation', 'stone-veneer-exposed-to-water', 'prepare-wall-for-stone-veneer', 'how-much-stone-veneer-to-order'].includes(article.id)) score += 18;
      return score;
    }

    const fields = [
      { value: article.title, weight: 42 },
      { value: article.intent, weight: 26 },
      { value: article.excerpt, weight: 22 },
      { value: `${article.category} ${article.cluster}`, weight: 14 },
      { value: article.terms, weight: 9 }
    ];

    let matched = 0;
    let score = 0;
    tokens.forEach((token) => {
      let tokenMatched = false;
      fields.forEach((field) => {
        const value = compact(field.value);
        if (!value.includes(token)) return;
        tokenMatched = true;
        score += field.weight;
        if (value.startsWith(token)) score += Math.round(field.weight * 0.35);
      });
      if (tokenMatched) matched += 1;
    });

    if (!matched) return -1;
    if (matched === tokens.length) score += 36;
    else score -= (tokens.length - matched) * 16;
    if (filter.id !== 'all' && filter.match(article)) score += 10;
    score += Math.max(0, 10 - Number(article.priority || 10));
    return score;
  }

  function articleSearchText(article) {
    return compact([
      article.title,
      article.intent,
      article.excerpt,
      article.category,
      article.cluster,
      article.terms
    ].filter(Boolean).join(' '));
  }

  function articleMatchGroup(article, query) {
    const tokens = tokenize(query);
    if (!tokens.length) return 'popular';
    const haystack = articleSearchText(article);
    const wordSet = new Set(haystack.split(/\s+/).filter(Boolean));
    const normalizedQuery = compact(query);
    const hasExactPhrase = tokens.length > 1 && normalizedQuery && haystack.includes(normalizedQuery);
    const hasEveryExactWord = tokens.every((token) => wordSet.has(token));
    if (hasExactPhrase || hasEveryExactWord) return 'exact';
    if (tokens.every((token) => haystack.includes(token))) return 'all_words';
    return 'related';
  }

  function groupedAutocompleteArticles(data, query, activeFilter, limit) {
    const queryHasTokens = tokenize(query).length > 0;
    const ranked = articles(data)
      .map((article) => ({
        article,
        score: autocompleteScore(article, query, activeFilter),
        group: articleMatchGroup(article, query)
      }))
      .filter((item) => item.score >= 0)
      .sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title));

    if (!queryHasTokens) {
      return [{
        id: 'popular',
        label: 'Popular answers',
        articles: ranked.slice(0, limit || 7).map((item) => item.article)
      }];
    }

    const groupOrder = [
      { id: 'exact', label: 'Exact word matches', max: 5 },
      { id: 'all_words', label: 'All words matched', max: 4 },
      { id: 'related', label: 'Related matches', max: 4 }
    ];
    const used = new Set();

    return groupOrder.map((group) => {
      const items = ranked
        .filter((item) => item.group === group.id && !used.has(item.article.id))
        .slice(0, group.max)
        .map((item) => {
          used.add(item.article.id);
          return item.article;
        });
      return { id: group.id, label: group.label, articles: items };
    }).filter((group) => group.articles.length);
  }

  function searchSuggestionMarkup(data, article, query, index, listId) {
    const tokens = tokenize(query);
    const snippet = snippetForArticle(article, tokens);
    return `<a class="rhl-search-suggestion" id="${escapeHtml(listId)}-option-${index}" role="option" aria-selected="false" href="${escapeHtml(articleUrl(article))}" data-rhl-suggestion-id="${escapeHtml(article.id)}" data-rhl-suggestion-index="${index}">
      <span class="rhl-search-suggestion__meta">${escapeHtml(labelForArticle(data, article))}</span>
      <strong>${highlightText(article.title, tokens)}</strong>
      ${snippet ? `<small>${highlightText(snippet, tokens)}</small>` : ''}
    </a>`;
  }

  function topicSuggestions(data, query, activeFilter) {
    const tokens = tokenize(query);
    const candidates = HOME_FILTERS
      .filter((filter) => filter.id !== 'all')
      .map((filter) => {
        const label = `${filter.label} answers`;
        const haystack = compact(`${filter.label} ${filter.title} ${filter.description}`);
        let score = filter.id === activeFilter ? 12 : 0;
        tokens.forEach((token) => {
          if (haystack.includes(token)) score += 18;
        });
        return { filter, label, score };
      })
      .filter((item) => !tokens.length ? item.filter.id === activeFilter : item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);

    return candidates.map((item) => `<button class="rhl-search-topic" type="button" data-rhl-suggestion-topic="${escapeHtml(item.filter.id)}">
      <span>${escapeHtml(item.label)}</span>
      <small>${articles(data).filter(item.filter.match).length} articles</small>
    </button>`).join('');
  }

  function wireSearchAutocomplete(root, data, options) {
    const opts = options || {};
    const forms = root.querySelectorAll('[data-rhl-search-form]');
    const activeFilter = opts.activeFilter || (() => 'all');

    forms.forEach((form, formIndex) => {
      const input = form.querySelector('[data-rhl-search], [data-rhl-top-search]');
      if (!input || form.dataset.rhlAutocompleteReady === 'true') return;
      form.dataset.rhlAutocompleteReady = 'true';
      form.classList.add('rhl-search-form--suggestions');
      const panel = document.createElement('div');
      const listId = `rhl-search-suggest-${formIndex}-${Math.random().toString(36).slice(2, 8)}`;
      panel.className = 'rhl-search-suggest';
      panel.id = listId;
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', 'Suggested help answers');
      panel.hidden = true;
      form.appendChild(panel);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', listId);

      let activeIndex = -1;
      let currentArticles = [];

      function close() {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      }

      function setActive(index) {
        const optionsNodes = Array.from(panel.querySelectorAll('[role="option"]'));
        activeIndex = optionsNodes.length ? (index + optionsNodes.length) % optionsNodes.length : -1;
        optionsNodes.forEach((node, nodeIndex) => {
          const isActive = nodeIndex === activeIndex;
          node.classList.toggle('is-active', isActive);
          node.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        if (activeIndex >= 0) input.setAttribute('aria-activedescendant', optionsNodes[activeIndex].id);
      }

      function render() {
        const query = input.value.trim();
        const filterId = activeFilter();
        const groups = groupedAutocompleteArticles(data, query, filterId, 9);
        currentArticles = groups.flatMap((group) => group.articles);
        const topics = topicSuggestions(data, query, filterId);
        let suggestionIndex = 0;
        const articleMarkup = groups.map((group) => {
          const rows = group.articles.map((article) => searchSuggestionMarkup(data, article, query, suggestionIndex++, listId)).join('');
          return `<div class="rhl-search-suggest__group rhl-search-suggest__group--${escapeHtml(group.id)}"><span>${escapeHtml(group.label)}</span>${rows}</div>`;
        }).join('');

        if (!articleMarkup && !topics) {
          panel.innerHTML = `<div class="rhl-search-empty">
            <strong>No strong match yet</strong>
            <small>Try a simpler phrase like install, water, fireplace, samples, or cost.</small>
          </div>`;
        } else {
          panel.innerHTML = `${articleMarkup || ''}
          ${topics ? `<div class="rhl-search-suggest__group rhl-search-suggest__group--topics"><span>Browse topics</span>${topics}</div>` : ''}`;
        }

        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        setActive(-1);
        track('rsv_help_search_suggestions', { query, result_count: currentArticles.length, mode: root.dataset.rhlMode || 'home' });
      }

      input.addEventListener('focus', render);
      input.addEventListener('input', render);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (panel.hidden) render();
          setActive(activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (panel.hidden) render();
          setActive(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          const active = panel.querySelector('[role="option"].is-active');
          if (active) {
            event.preventDefault();
            active.click();
          }
        } else if (event.key === 'Escape') {
          close();
        }
      });

      panel.addEventListener('mousedown', (event) => event.preventDefault());
      panel.addEventListener('click', (event) => {
        const topic = event.target.closest('[data-rhl-suggestion-topic]');
        if (topic) {
          const filterId = topic.getAttribute('data-rhl-suggestion-topic');
          if (opts.onTopic) opts.onTopic(filterId, input.value.trim());
          close();
          return;
        }

        const suggestion = event.target.closest('[data-rhl-suggestion-id]');
        if (!suggestion) return;
        const articleId = suggestion.getAttribute('data-rhl-suggestion-id');
        pushRecent(articleId);
        track('rsv_help_suggestion_click', {
          article_id: articleId,
          query: input.value.trim(),
          mode: root.dataset.rhlMode || 'home'
        });
      });

      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) close();
      });
    });
  }

  function filteredArticles(data, query, activeFilter) {
    return articles(data)
      .map((article) => ({ article, score: scoreArticle(article, query, activeFilter) }))
      .filter((item) => item.score >= 0)
      .sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title))
      .map((item) => item.article);
  }

  function homeFilterButton(filter, activeId, count) {
    const active = filter.id === activeId ? ' is-active' : '';
    return `<button class="rhl-filter-button${active}" type="button" data-rhl-home-filter="${escapeHtml(filter.id)}">
      <span>${escapeHtml(filter.label)}</span>
      <small>${count}</small>
    </button>`;
  }

  function articleCard(data, article) {
    const label = categoryLabel(data, article.categoryId, article.category);
    return `<a class="rhl-answer-card" href="${escapeHtml(articleUrl(article))}" data-rhl-card-id="${escapeHtml(article.id)}">
      <span class="rhl-answer-card__kicker">${escapeHtml(label)}</span>
      <h3>${escapeHtml(article.title)}</h3>
      <p>${escapeHtml(article.excerpt)}</p>
    </a>`;
  }

  function compactLink(article, reason) {
    return `<a class="rhl-compact-link" href="${escapeHtml(articleUrl(article))}" data-rhl-next-id="${escapeHtml(article.id)}">
      ${reason ? `<span>${escapeHtml(reason)}</span>` : ''}
      <strong>${escapeHtml(article.title)}</strong>
    </a>`;
  }

  function currentCategory(data, current) {
    const categories = data.navigation?.categories || [];
    return categories.find((category) => (
      category.id === current.categoryId ||
      category.label === current.category ||
      (category.articles || []).some((article) => article.id === current.id)
    )) || null;
  }

  function currentClusterId(current) {
    return current.clusterId || current.cluster;
  }

  function libraryArticleLink(article, currentId) {
    const isActive = article.id === currentId ? ' is-active' : '';
    const status = article.reviewStatus && article.reviewStatus !== 'published'
      ? `<small>${escapeHtml(article.reviewStatus.replace(/-/g, ' '))}</small>`
      : '';
    return `<a class="rhl-library-article${isActive}" href="${escapeHtml(articleUrl(article))}" data-rhl-next-id="${escapeHtml(article.id)}">
      <span>${escapeHtml(article.title)}</span>
      ${status}
    </a>`;
  }

  function questionLibraryMarkup(data, current, options) {
    const opts = options || {};
    const category = currentCategory(data, current);
    const categories = data.navigation?.categories || [];
    const categoryArticles = category?.articles || articles(data).filter((article) => article.categoryId === current.categoryId);
    const categoryName = category?.label || categoryLabel(data, current.categoryId, current.category) || 'Help answers';
    const categoryHref = `${opts.searchUrl || '/pages/help-library-preview'}?q=${encodeURIComponent(categoryName)}`;
    const activeCluster = currentClusterId(current);
    const clusterMarkup = (category?.clusters || []).map((cluster) => {
      const clusterOpen = cluster.id === activeCluster || (cluster.articles || []).some((article) => article.id === current.id);
      const visible = (cluster.articles || []).slice(0, opts.compact ? 8 : 10);
      return `<section class="rhl-library-group${clusterOpen ? ' is-current' : ''}">
        <h3>${escapeHtml(cluster.label)}</h3>
        <div class="rhl-library-links">
          ${visible.map((article) => libraryArticleLink(article, current.id)).join('')}
        </div>
      </section>`;
    }).join('');

    const categorySwitch = categories
      .filter((item) => item.id !== category?.id)
      .slice(0, 5)
      .map((item) => `<a href="${escapeHtml(`${opts.searchUrl || '/pages/help-library-preview'}?q=${encodeURIComponent(item.label)}`)}">${escapeHtml(item.label)}</a>`)
      .join('');

    const search = opts.includeSearch ? `<form class="rhl-library-search rhl-library-search--sheet" data-rhl-search-form>
      <label>
        <span class="visually-hidden">Search the help library</span>
        <input data-rhl-search type="search" autocomplete="off" placeholder="Search answers">
      </label>
    </form>` : '';

    return `<div class="rhl-library-panel">
      ${search}
      <div class="rhl-library-current">
        <span>Current topic</span>
        <strong>${escapeHtml(categoryName)}</strong>
        <small>${categoryArticles.length} answers</small>
      </div>
      <div class="rhl-library-groups">
        ${clusterMarkup || categoryArticles.slice(0, 10).map((article) => libraryArticleLink(article, current.id)).join('')}
      </div>
      <a class="rhl-library-view-all" href="${escapeHtml(categoryHref)}">Browse all ${escapeHtml(categoryName.toLowerCase())} answers</a>
      ${categorySwitch ? `<details class="rhl-library-other"><summary>Other topics</summary><div>${categorySwitch}</div></details>` : ''}
    </div>`;
  }

  function tree(data, currentId, limit) {
    const categories = data.navigation?.categories || [];
    return `<div class="rhl-tree">${categories.map((category) => {
      const clusters = (category.clusters || []).map((cluster) => {
        const items = (cluster.articles || []).slice(0, limit || 8).map((article) => {
          const isActive = article.id === currentId ? ' is-active' : '';
          return `<a class="rhl-tree__link${isActive}" href="${escapeHtml(articleUrl(article))}">${escapeHtml(article.title)}</a>`;
        }).join('');
        return `<div class="rhl-tree__cluster">
          <div class="rhl-tree__cluster-title">${escapeHtml(cluster.label)}</div>
          ${items}
        </div>`;
      }).join('');
      return `<details ${category.articles.some((article) => article.id === currentId) ? 'open' : ''}>
        <summary>${escapeHtml(category.label)} <span>${category.articles.length}</span></summary>
        ${clusters}
      </details>`;
    }).join('')}</div>`;
  }

  function recommendations(data, currentId, limit) {
    const direct = data.recommendations?.[currentId] || [];
    if (direct.length) return direct.slice(0, limit || 4);
    const current = articleByIdOrSlug(data, currentId);
    if (!current) return articles(data).slice(0, limit || 4).map((article) => ({ ...article, reason: 'Start here' }));
    return articles(data)
      .filter((article) => article.id !== current.id && (article.categoryId === current.categoryId || article.clusterId === current.clusterId))
      .slice(0, limit || 4)
      .map((article) => ({ ...article, reason: 'Related answer' }));
  }

  function uniqueArticles(list) {
    const seen = new Set();
    return list.filter((article) => {
      if (!article?.id || seen.has(article.id)) return false;
      seen.add(article.id);
      return true;
    });
  }

  function renderHome(root, data) {
    const state = {
      filter: 'all',
      query: new URLSearchParams(window.location.search).get('q') || ''
    };

    const filterTarget = root.querySelector('[data-rhl-home-filters]');
    const resultTarget = root.querySelector('[data-rhl-results]');
    const countTarget = root.querySelector('[data-rhl-result-count]');
    const titleTarget = root.querySelector('[data-rhl-active-title]');
    const descriptionTarget = root.querySelector('[data-rhl-active-description]');
    const mobileActive = root.querySelector('[data-rhl-mobile-active]');
    const mobileLibrary = root.querySelector('[data-rhl-mobile-library]');
    const searchInputs = root.querySelectorAll('[data-rhl-search], [data-rhl-top-search]');
    let gridHasPainted = false;
    let gridPaintTimer = null;

    function countsForFilters() {
      return HOME_FILTERS.reduce((acc, filter) => {
        acc[filter.id] = filter.id === 'all' ? articles(data).length : articles(data).filter(filter.match).length;
        return acc;
      }, {});
    }

    function paintFilters() {
      if (!filterTarget) return;
      const counts = countsForFilters();
      filterTarget.innerHTML = HOME_FILTERS.map((filter) => homeFilterButton(filter, state.filter, counts[filter.id])).join('');
    }

    function paint() {
      const active = HOME_FILTERS.find((filter) => filter.id === state.filter) || HOME_FILTERS[0];
      const found = filteredArticles(data, state.query, state.filter);
      const display = found.slice(0, state.filter === 'all' && !state.query ? 9 : 24);

      if (titleTarget) titleTarget.textContent = active.title;
      if (descriptionTarget) descriptionTarget.textContent = active.description;
      if (mobileActive) mobileActive.textContent = active.title;
      if (countTarget) countTarget.textContent = `${found.length} answers`;
      if (resultTarget) {
        const nextMarkup = display.length
          ? display.map((article) => articleCard(data, article)).join('')
          : '<div class="rhl-empty"><h3>No exact match yet</h3><p>Try a simpler term, or use this as a missing-answer signal for the editorial queue.</p></div>';

        const renderGrid = () => {
          resultTarget.innerHTML = nextMarkup;
          resultTarget.classList.remove('rhl-card-grid--updating');
          resultTarget.classList.remove('rhl-card-grid--entered');
          requestAnimationFrame(() => resultTarget.classList.add('rhl-card-grid--entered'));
          gridHasPainted = true;
        };

        if (!gridHasPainted || prefersReducedMotion()) {
          renderGrid();
        } else {
          window.clearTimeout(gridPaintTimer);
          resultTarget.classList.add('rhl-card-grid--updating');
          gridPaintTimer = window.setTimeout(renderGrid, 45);
        }
      }
    }

    searchInputs.forEach((input) => {
      input.value = state.query;
      input.addEventListener('input', () => {
        state.query = input.value.trim();
        searchInputs.forEach((other) => {
          if (other !== input) other.value = input.value;
        });
        track('rsv_help_search', { query: state.query, mode: 'home' });
        paint();
      });
    });

    wireSearchAutocomplete(root, data, {
      activeFilter: () => state.filter,
      onTopic: (filterId, query) => {
        state.filter = filterId || state.filter;
        state.query = query || state.query;
        searchInputs.forEach((input) => {
          input.value = state.query;
        });
        paintFilters();
        paint();
      }
    });

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rhl-home-filter]');
      if (!button) return;
      state.filter = button.getAttribute('data-rhl-home-filter') || 'all';
      root.querySelectorAll('[data-rhl-home-filter]').forEach((node) => node.classList.toggle('is-active', node === button));
      track('rsv_help_filter', { filter: state.filter });
      paint();
    });

    root.addEventListener('click', (event) => {
      const card = event.target.closest('[data-rhl-card-id]');
      if (!card) return;
      pushRecent(card.getAttribute('data-rhl-card-id'));
      track('rsv_help_card_click', { article_id: card.getAttribute('data-rhl-card-id'), filter: state.filter });
    });

    root.querySelectorAll('[data-rhl-search-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = form.querySelector('input');
        state.query = input ? input.value.trim() : state.query;
        paint();
      });
    });

    if (mobileLibrary) mobileLibrary.innerHTML = tree(data, '', 6);
    paintFilters();
    paint();
  }

  function renderArticle(root, data) {
    let current = articleByIdOrSlug(data, root.dataset.currentId) || articleByIdOrSlug(data, root.dataset.currentSlug);
    if (!current) current = articles(data)[0];
    if (!current) return;

    const currentId = current.id;
    pushRecent(currentId);

    const categoryTarget = root.querySelector('[data-rhl-category-label]');
    const kickerTarget = root.querySelector('[data-rhl-article-kicker]');
    const titleTarget = root.querySelector('[data-rhl-article-title]');
    const summaryTarget = root.querySelector('[data-rhl-article-summary]');
    const shortAnswerTarget = root.querySelector('[data-rhl-short-answer]');
    const relatedRow = root.querySelector('[data-rhl-related-row]');
    const desktopLibrary = root.querySelector('[data-rhl-article-library]');
    const mobileLibrary = root.querySelector('[data-rhl-mobile-library]');
    const mobileContext = root.querySelector('[data-rhl-mobile-context]');
    const mobileSheetTitle = root.querySelector('[data-rhl-mobile-sheet-title]');
    const recs = recommendations(data, currentId, 5);
    const nearby = articles(data)
      .filter((article) => article.id !== currentId && (article.categoryId === current.categoryId || article.clusterId === current.clusterId))
      .slice(0, 5);
    const nextArticle = recs[0];
    const related = uniqueArticles([nextArticle, ...recs.slice(1), ...nearby])
      .filter((article) => article?.id && article.id !== currentId)
      .slice(0, 3);

    const currentCategoryLabel = categoryLabel(data, current.categoryId, current.category);
    if (categoryTarget) categoryTarget.textContent = currentCategoryLabel;
    if (mobileContext) mobileContext.textContent = currentCategoryLabel ? `${currentCategoryLabel} · ${articles(data).filter((article) => article.categoryId === current.categoryId).length} answers` : 'Help answers';
    if (mobileSheetTitle) mobileSheetTitle.textContent = currentCategoryLabel || 'Help answers';
    if (kickerTarget) kickerTarget.textContent = current.cluster || categoryLabel(data, current.categoryId, current.category) || 'Stone veneer answer';
    if (titleTarget && (titleTarget.textContent.trim() === current.id || titleTarget.textContent.trim() === current.slug)) {
      titleTarget.textContent = current.title;
    }
    if (summaryTarget && current.excerpt) {
      const existing = summaryTarget.textContent.trim();
      if (!existing || existing.includes('connected to the help-library registry')) summaryTarget.textContent = current.excerpt;
    }
    if (shortAnswerTarget && current.excerpt) {
      const existing = shortAnswerTarget.textContent.trim();
      if (!existing || existing.includes('staged from the help registry')) shortAnswerTarget.textContent = current.excerpt;
    }
    if (relatedRow) relatedRow.innerHTML = related.map((article) => compactLink(article, 'Related answer')).join('');
    if (desktopLibrary) desktopLibrary.innerHTML = questionLibraryMarkup(data, current, { searchUrl: root.dataset.searchUrl, compact: false });
    if (mobileLibrary) {
      mobileLibrary.innerHTML = questionLibraryMarkup(data, current, { searchUrl: root.dataset.searchUrl, compact: true, includeSearch: true });
    }

    wireSearchAutocomplete(root, data, {
      activeFilter: () => HOME_FILTERS.find((filter) => filter.match(current))?.id || 'all',
      onTopic: (filterId, query) => {
        const filter = HOME_FILTERS.find((item) => item.id === filterId);
        const q = query || filter?.label || '';
        window.location.href = `${root.dataset.searchUrl || '/pages/help-library-preview'}?q=${encodeURIComponent(q)}`;
      }
    });

    root.querySelectorAll('[data-rhl-search-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = form.querySelector('input');
        const q = input ? input.value.trim() : '';
        if (q) window.location.href = `${root.dataset.searchUrl || '/pages/help-library-preview'}?q=${encodeURIComponent(q)}`;
      });
    });

    root.addEventListener('click', (event) => {
      const next = event.target.closest('[data-rhl-next-id]');
      if (!next) return;
      track('rsv_help_next_click', {
        from_article_id: currentId,
        to_article_id: next.getAttribute('data-rhl-next-id')
      });
    });

    track('rsv_help_article_view', {
      article_id: currentId,
      category: current.categoryId,
      cluster: current.clusterId,
      journey_stage: current.journeyStage,
      review_status: current.reviewStatus
    });
  }

  function wireSheet(root) {
    const openers = root.querySelectorAll('[data-rhl-open-sheet]');
    const closers = root.querySelectorAll('[data-rhl-close-sheet]');
    const sheet = root.querySelector('[data-rhl-sheet]');
    const backdrop = root.querySelector('[data-rhl-sheet-backdrop]');
    const mobileBar = root.querySelector('.rhl-mobile-bar__inner');
    const dragHandle = root.querySelector('[data-rhl-sheet-handle]');
    if (!sheet || !backdrop || !openers.length) return;
    const drag = {
      active: false,
      startY: 0,
      currentY: 0,
      pointerId: null,
      mode: 'sheet'
    };

    function setDragOffset(offset) {
      sheet.style.setProperty('--rhl-sheet-drag', `${Math.max(0, Math.round(offset))}px`);
    }

    function setOpen(open) {
      if (!open) setDragOffset(0);
      sheet.classList.toggle('is-open', open);
      backdrop.classList.toggle('is-open', open);
      sheet.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('rhl-is-locked', open);
      if (open) {
        const close = sheet.querySelector('[data-rhl-close-sheet]');
        if (close) close.focus({ preventScroll: true });
      }
    }

    function endDrag(event) {
      if (!drag.active) return;
      const distance = drag.currentY - drag.startY;
      const shouldOpen = drag.mode === 'bar' && distance < -34;
      const shouldClose = drag.mode === 'sheet' && distance > Math.min(160, sheet.offsetHeight * 0.28);
      const shouldConsume = drag.mode === 'sheet' || shouldOpen || shouldClose;

      drag.active = false;
      sheet.classList.remove('is-dragging');
      if (drag.pointerId !== null && sheet.releasePointerCapture) {
        try {
          sheet.releasePointerCapture(drag.pointerId);
        } catch (error) {}
      }
      drag.pointerId = null;

      if (shouldClose) setOpen(false);
      else if (shouldOpen) setOpen(true);
      else setDragOffset(0);

      if (event && shouldConsume) event.preventDefault();
    }

    function moveDrag(event) {
      if (!drag.active) return;
      drag.currentY = event.clientY;
      const distance = drag.currentY - drag.startY;

      if (drag.mode === 'sheet') {
        setDragOffset(distance > 0 ? distance : distance * 0.18);
      }

      event.preventDefault();
    }

    function beginSheetDrag(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, a, input, textarea, select') && !event.target.closest('[data-rhl-sheet-handle]')) return;
      drag.active = true;
      drag.mode = 'sheet';
      drag.startY = event.clientY;
      drag.currentY = event.clientY;
      drag.pointerId = event.pointerId;
      sheet.classList.add('is-dragging');
      if (sheet.setPointerCapture) sheet.setPointerCapture(event.pointerId);
      event.preventDefault();
    }

    function beginBarDrag(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      drag.active = true;
      drag.mode = 'bar';
      drag.startY = event.clientY;
      drag.currentY = event.clientY;
      drag.pointerId = event.pointerId;
    }

    openers.forEach((button) => button.addEventListener('click', () => setOpen(true)));
    closers.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    backdrop.addEventListener('click', () => setOpen(false));
    if (dragHandle) dragHandle.addEventListener('pointerdown', beginSheetDrag);
    if (mobileBar) mobileBar.addEventListener('pointerdown', beginBarDrag);
    window.addEventListener('pointermove', moveDrag, { passive: false });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheet.classList.contains('is-open')) setOpen(false);
    });
  }

  function init(root) {
    loadData(root.dataset.libraryUrl)
      .then((data) => {
        wireSheet(root);
        if (root.dataset.rhlMode === 'article') renderArticle(root, data);
        else renderHome(root, data);
        wireNavigationMotion(root);
        markReady(root);
      })
      .catch((error) => {
        const target = root.querySelector('[data-rhl-results]') || root;
        target.insertAdjacentHTML('beforeend', `<div class="rhl-empty"><h3>Help data did not load</h3><p>${escapeHtml(error.message)}</p></div>`);
      });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-rhl-root]').forEach(init);
  });
})();
